import { mdiClose } from '@mdi/js';
import Icon from '@mdi/react';
import { Button, MenuItem, Select, Stack, SxProps } from '@mui/material';
import React, { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fullSize } from '../../../Constants';
import { IAppReduxStore } from '../../../redux/store';
import { headingFontBold, vars } from '../../../Theme';
import AppTooltip from '../../General/AppTooltip/AppTooltip';
import ViewControls from '../ViewControls';
import ModalResultTable from '../../General/ModalTable/ModalResultTable';
import { getProjectDocumentSummary } from '../../../redux/project/documents/sliceProjectSelectedDocuments';
import SelectedDocumentsIndicator from './SelectedDocumentsIndicator';

/** Base button styles. */
const buttonSx: SxProps = {
    fontFamily: 'inherit',
    textTransform: 'none',
    alignItems: 'center',
    fontSize: 'inherit',
    color: 'inherit',
    borderRadius: 2,
    minWidth: 24,
    p: 1,
};

/**
 * Special view that shows information on the selected documents in a project.
 */
export default ({ close }: { close: React.MouseEventHandler }) => {
    /** Configure component translation. */
    const { i18n, t } = useTranslation('translations', { keyPrefix: 'projectView' });

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Access relevant state variables from the redux store. */
    const fieldSchema = useSelector((store: IAppReduxStore) => store.projectViewData.schema.data);
    const fields = useSelector((store: IAppReduxStore) => store.projectViewData.fields.data);
    const documentIds = useSelector(
        (store: IAppReduxStore) => store.projectSelectedDocuments.storage.documentIds,
    );

    const documentSummary = useSelector(
        (store: IAppReduxStore) => store.projectSelectedDocuments.data,
    );
    const summaryLoading = useSelector(
        (store: IAppReduxStore) => store.projectSelectedDocuments.loading,
    );
    const viewData = useSelector((store: IAppReduxStore) => store.projectViews.data);

    /** State slice that stores the sorting type for selected documents. */
    const [activeSort, setActiveSort] = useState<string>('');

    /** Get the replicaId, areaName and viewName from the react router. */
    const { replicaId, areaName, viewName } = useParams();

    /**
     * Effect that sets the starting active document sorting type to the user's document view.
     */
    useEffect(() => {
        updateSortType(viewName);
    }, [viewName]);

    /**
     * Updates the sorting type and fetches the summary data for the selected documents.
     * @param type -> New sorting type.
     */
    const updateSortType = useCallback(
        (type: string) => {
            const columnName = type.split('.').pop();

            dispatch(
                getProjectDocumentSummary({
                    fields: [].concat(
                        ...fieldSchema.map(
                            uf => fields.filter(x => x.field === uf)[0]?.fields ?? [],
                        ),
                    ),
                    viewName: viewData[`${areaName}.${type}`].viewStructure,
                    columnName,
                    documentIds,
                    replicaId,
                }),
            );

            setActiveSort(columnName);
        },
        [fieldSchema, fields, areaName, viewData],
    );

    /** Calculate the number of selected documents. */
    const totalDocuments = documentIds.length;

    /** Styles for normal and bold text. */
    const textSx = { ...headingFontBold, fontSize: 14 };
    const textSxBold = { ...headingFontBold, fontSize: 14 };

    /** Create the menu options for the select component. */
    const opts = useMemo(
        () =>
            Object.keys(viewData)
                .filter(x => viewData[x].areaId === areaName)
                .map(x => x.split('.').pop())
                .map(x => (
                    <MenuItem key={x} value={x} sx={textSx}>
                        {viewData[`${areaName}.${x}`].translations[i18n.language]}
                    </MenuItem>
                )),
        [i18n.language],
    );

    /**
     * Creates the groupings for the selected documents, based on the active sort type..
     */
    const getContent = () => {
        /** Don't show anything if summary data is still being loaded. */
        if (summaryLoading || !documentSummary?.documents?.length) {
            return <></>;
        }

        const groups = {};

        documentSummary?.documents?.forEach(x => {
            /** Determine group name.  */
            const groupName = x[activeSort]?.length
                ? x[activeSort]
                : /** If a document doesn't have a value for the sort field, it will be added to the "unassigned" group. */
                  t('unassigned');

            /** Create empty group, if it doesn't already exist. */
            if (!groups[groupName]) {
                groups[groupName] = [];
            }

            /** Add document to group. */
            groups[groupName].push(x);
        });

        return (
            Object
                /** Get group names. */
                .keys(groups)
                /** Sort groups alphabetically. */
                .sort()
                /** Map groups to a header text and a DataGrid. */
                .map(x => (
                    <Stack key={x}>
                        <Stack sx={{ ...headingFontBold, fontSize: 18, py: 1 }}>{x}</Stack>
                        <ModalResultTable documents={groups[x]} />
                    </Stack>
                ))
        );
    };

    return (
        <Stack
            direction='column'
            sx={{
                flexGrow: 4,
                ...fullSize,
            }}
        >
            <ViewControls
                header={
                    <Stack
                        direction='row'
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        <AppTooltip title={t('hide')} placement='top-start'>
                            <Button sx={buttonSx} onClick={close}>
                                <Icon path={mdiClose} size={1} color={vars.font} />
                            </Button>
                        </AppTooltip>
                        {t('SelectedDocuments')} ({totalDocuments})
                    </Stack>
                }
                row2={
                    <Stack
                        direction='row'
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                            flexGrow: 4,
                            px: 1,
                            py: 1,
                        }}
                    >
                        <Stack sx={{ ...textSxBold, pl: 1 }}>{t('sortBy')}</Stack>

                        <Select
                            size='small'
                            value={activeSort}
                            defaultValue={activeSort}
                            onChange={e => updateSortType(e.target.value)}
                            sx={textSx}
                        >
                            {opts}
                        </Select>
                    </Stack>
                }
            />
            <Stack spacing={1} sx={{ flexGrow: 4, px: 2, py: 1, overflowY: 'auto' }}>
                {getContent()}
            </Stack>
            <SelectedDocumentsIndicator showBackButton onClick={close} />
        </Stack>
    );
};
